import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Plus, Trash2, Eye, EyeOff } from "lucide-react";
import { Modal, Field, inputCls, Btn, ConfirmDialog } from "./kit";

const EMPTY = { label: "", value: "", visible: true, order: 0 };

export default function AdminStatistics() {
  const [items, setItems] = useState([]);
  const [modal, setModal] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editing, setEditing] = useState(null);
  const [confirm, setConfirm] = useState(null);

  const load = async () => { try { const r = await api.get("/admin/statistics"); setItems(r.data); } catch (e) {} };
  useEffect(() => { load(); }, []);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    if (!form.label || !form.value) { toast.error("Label and value required"); return; }
    try {
      const payload = { ...form, order: parseInt(form.order) || 0 };
      if (editing) await api.put(`/admin/statistics/${editing}`, payload); else await api.post("/admin/statistics", payload);
      toast.success("Saved"); setModal(false); load();
    } catch (e) { toast.error("Failed"); }
  };
  const toggle = async (s) => { try { await api.put(`/admin/statistics/${s.id}`, { visible: !s.visible }); load(); } catch (e) {} };
  const del = async () => { try { await api.delete(`/admin/statistics/${confirm}`); toast.success("Deleted"); setConfirm(null); load(); } catch (e) { toast.error("Failed"); } };

  return (
    <div>
      <div className="mb-6 flex items-center justify-between">
        <div><h1 className="font-display text-2xl font-extrabold text-offwhite">Statistics</h1><p className="mt-1 font-body text-sm text-offwhite/55">Control homepage & export numbers. Hidden stats are never shown publicly.</p></div>
        <Btn onClick={() => { setForm(EMPTY); setEditing(null); setModal(true); }} data-testid="add-stat-btn"><Plus className="mr-1 inline h-4 w-4" /> Add Statistic</Btn>
      </div>
      <p className="mb-4 rounded-lg bg-amber-brand/10 px-4 py-2 font-body text-xs text-amber-soft">Only enter numbers you can verify. Do not fabricate shipments, volumes, or markets.</p>
      {items.length === 0 ? <div className="rounded-xl border border-dashed border-offwhite/15 py-16 text-center font-body text-sm text-offwhite/40">No statistics. Nothing will be shown publicly.</div> : (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {items.map((s) => (
            <div key={s.id} className={`rounded-xl border p-5 ${s.visible ? "border-offwhite/10 bg-[#101c14]" : "border-offwhite/5 bg-[#101c14]/50 opacity-60"}`}>
              <div className="font-display text-2xl font-extrabold text-amber-soft">{s.value}</div>
              <div className="mt-1 font-mono text-[10px] uppercase tracking-wider text-offwhite/55">{s.label}</div>
              <div className="mt-4 flex gap-1">
                <button onClick={() => { setForm({ ...EMPTY, ...s }); setEditing(s.id); setModal(true); }} className="rounded px-2 py-1 font-mono text-[10px] uppercase text-offwhite/60 hover:bg-offwhite/10">Edit</button>
                <button onClick={() => toggle(s)} className="rounded p-1 text-offwhite/60 hover:bg-offwhite/10">{s.visible ? <Eye className="h-3.5 w-3.5" /> : <EyeOff className="h-3.5 w-3.5" />}</button>
                <button onClick={() => setConfirm(s.id)} className="rounded p-1 text-rose-300 hover:bg-rose-500/10"><Trash2 className="h-3.5 w-3.5" /></button>
              </div>
            </div>
          ))}
        </div>
      )}
      <Modal open={modal} onClose={() => setModal(false)} title={editing ? "Edit Statistic" : "Add Statistic"}>
        <div className="space-y-4">
          <Field label="Label"><input data-testid="stat-label" className={inputCls} value={form.label} onChange={(e) => set("label", e.target.value)} placeholder="e.g. International Shipments" /></Field>
          <Field label="Value"><input data-testid="stat-value" className={inputCls} value={form.value} onChange={(e) => set("value", e.target.value)} placeholder="e.g. 40+" /></Field>
          <Field label="Order"><input type="number" className={inputCls} value={form.order} onChange={(e) => set("order", e.target.value)} /></Field>
          <label className="flex items-center gap-2 font-body text-sm text-offwhite/80"><input type="checkbox" checked={form.visible} onChange={(e) => set("visible", e.target.checked)} className="h-4 w-4 accent-forest" /> Visible publicly</label>
          <div className="flex justify-end gap-3"><Btn variant="ghost" onClick={() => setModal(false)}>Cancel</Btn><Btn onClick={save} data-testid="save-stat-btn">Save</Btn></div>
        </div>
      </Modal>
      <ConfirmDialog open={!!confirm} onClose={() => setConfirm(null)} onConfirm={del} message="Delete this statistic?" />
    </div>
  );
}
